import { DataSourceType } from "@coeff/api";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";

import {
  CoePlanIcon,
  FlexColumn,
  Typography,
  BulletCheckList,
  FlexRow,
  ImportTypeIcon,
  Button,
} from "../../../components";
import { COLORS } from "../../../constants";
import { AppState } from "../../../store";
import { useTrack } from "../../../utils";
import { dataSourceTypeToCommaSeparatedString, submitContactSalesForm } from "../billing.utils";

import { RequestSentModal } from "./RequestSentModal";
type Props = {
  premiumConnectorsUsed: DataSourceType[];
};
const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr 426px;
  width: 100%;
  height: 100%;
`;

const LogoContainer = styled.div`
  background: linear-gradient(
    103deg,
    #428bfa 0%,
    #a769e6 35.27%,
    #d850b8 60.74%,
    #ff3353 78.38%,
    #ff8121 96.5%
  );
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;
`;

const RefreshStoppedWarning = styled.div`
  line-height: 1.8em;
`;

export const UpgradeToEnterpriseFullScreen: React.FC<Props> = ({ premiumConnectorsUsed }) => {
  const track = useTrack();
  const hasPremiumConnectors = premiumConnectorsUsed.length > 0;

  useEffect(() => {
    track("upgrade_to_enterprise_screen_viewed");
  }, []);

  const clientFlags = useSelector((state: AppState) => state.app.clientFlags);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [showSentModal, setShowSentModal] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  // ENG-8924: Contact Sales files the request straight away (no free-text form) and confirms with
  // "Request Sent", matching the add-on. The default message gives the HubSpot lead context.
  const handleContactSales = async () => {
    track("contact_sales_button_clicked");
    setSending(true);
    setError(undefined);
    const comment = hasPremiumConnectors
      ? `Requesting to upgrade to an Enterprise plan to import from ${dataSourceTypeToCommaSeparatedString(
          premiumConnectorsUsed,
          2
        )}.`
      : "Requesting to upgrade to an Enterprise plan.";
    const isSent = await submitContactSalesForm(comment, clientFlags);
    setSending(false);
    if (isSent) {
      setSent(true);
      setShowSentModal(true);
    } else {
      setError("Something went wrong. Please try again.");
    }
  };

  return (
    <Layout>
      <FlexColumn gap={2} padding={"40px"}>
        {/* One "Upgrade" title for every gate page (10/1 design review). */}
        <Typography variant={"h4"} fontWeight={"bold"}>
          Upgrade
        </Typography>
        {hasPremiumConnectors ? (
          <Typography variant={"body1"}>
            Some users on your team are using premium data sources (
            {dataSourceTypeToCommaSeparatedString(premiumConnectorsUsed, 2)}) that requires a plan
            upgrade.
          </Typography>
        ) : (
          <Typography variant={"body1"}>
            Your organization already has a Coefficient plan. Contact sales to be added.
          </Typography>
        )}

        <BulletCheckList
          items={[
            <Typography variant={"body2"}>
              {hasPremiumConnectors
                ? `Connect to ${dataSourceTypeToCommaSeparatedString(
                    premiumConnectorsUsed,
                    2
                  )} and all premium data sources`
                : "Connect to all premium data sources"}
            </Typography>,
            <Typography variant={"body2"}>Admin controls & user permissions</Typography>,
            <Typography variant={"body2"}>For teams with 5+ users</Typography>,
            <Typography variant={"body2"}>Unlimited run usage + much more</Typography>,
          ]}
        ></BulletCheckList>
        {hasPremiumConnectors && (
          <FlexRow
            style={{
              backgroundColor: COLORS.yellowBg4,
              borderColor: COLORS.yellowBg4,
              padding: "12px",
              maxWidth: "534px",
            }}
            gap={1.5}
          >
            <FlexColumn style={{ maxWidth: "408px" }}>
              <Typography variant={"body2"} fontWeight={"bold"}>
                Upgrade to import from premium data sources
              </Typography>
              <Typography variant={"body2"} fontWeight={"normal"}>
                Some users on your team are using premium data sources (
                {dataSourceTypeToCommaSeparatedString(premiumConnectorsUsed, 2)}) that require a
                plan upgrade.
              </Typography>
            </FlexColumn>
            <FlexRow style={{ flex: 1 }} reverse>
              {premiumConnectorsUsed.slice(0, 2).map(ds => (
                <ImportTypeIcon type={ds} key={ds} size={40} />
              ))}
            </FlexRow>
          </FlexRow>
        )}
        {error && (
          <Typography variant={"body2"} style={{ color: COLORS.red }}>
            {error}
          </Typography>
        )}
        <Button
          type={"primary"}
          style={{ maxWidth: "180px", marginTop: "20px" }}
          loading={sending}
          disabled={sent}
          onClick={handleContactSales}
        >
          {sent ? "Request Sent" : "Contact Sales"}
        </Button>
      </FlexColumn>
      <LogoContainer>
        <CoePlanIcon height={88} width={76} fill={COLORS.white} />
      </LogoContainer>
      <RequestSentModal
        visible={showSentModal}
        message={"Our sales team will reach out with more information about upgrading your plan."}
        onClose={() => setShowSentModal(false)}
      />
    </Layout>
  );
};
